import type { HeaderStat, WidgetDataContract } from "@nexus/widget-sdk";

/**
 * `category-heatmap-table` v1 — a cross-tab of per-row counts across labeled
 * column groups, each column tinted or colored on its own, with a hover
 * breakdown of the items behind a count and a totals footer.
 *
 * Generic on purpose: nothing here names "anomaly", "severity" or "status" —
 * a source supplies its own column groups (with a color token per column and
 * whether that column should tint by relative count) and its own rows. The
 * first consumer is the campaign report's system-anomalies matrix
 * (severity x status), but the shape fits any "how many of these fall into
 * each of two categorizations, per entity" need. See "Reuse before creating
 * a widget" in `WIDGET_AUTHORING.md` before adding a sibling widget for a
 * case this could serve instead.
 */
export const CATEGORY_HEATMAP_TABLE_CONTRACT: WidgetDataContract = {
  name: "category-heatmap-table",
  version: 1,
};

export interface HeatmapItem {
  label: string;
  /** ISO 8601 timestamp shown in the hover breakdown. */
  timestamp: string;
}

export interface HeatmapColumn {
  key: string;
  label: string;
  /** A design token, e.g. `"--sev-critical"` (without `var()`). */
  colorToken: string;
  /**
   * `true`: the cell's background tints by this column's count relative to
   * the busiest row (a heatmap column, like severity). `false`: the count is
   * shown in the column's flat color with no background tint (a status
   * column). Defaults to `false`.
   */
  heat?: boolean;
}

export interface HeatmapColumnGroup {
  id: string;
  label: string;
  columns: HeatmapColumn[];
}

export interface HeatmapRow {
  id: string;
  label: string;
  /** Keyed by column key; a column with no items for this row is omitted or `[]`. */
  cells: Record<string, HeatmapItem[]>;
}

export interface CategoryHeatmapTableData {
  columnGroups: HeatmapColumnGroup[];
  rows: HeatmapRow[];
  /** Optional headline figures, shown in the header when the instance is collapsible. */
  summary?: HeaderStat[];
}
